import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { Category, Transaction } from '@domain/finance'
import { useDeviceFlag } from '@ui/shell/device-preference'
import { Donut } from './Donut'
import { anelDuplo, noCartao } from './category-shares'

/**
 * "Gastos por categoria", com o recorte do cartão e os dois anéis.
 *
 * Mora aqui, e não no Dashboard, porque a mesma coisa aparece em dois lugares:
 * o mês corrente no Dashboard e o mês escolhido em Transações (decisão 293). A
 * segunda cópia era o caminho mais curto e o mais caro: duas roscas que
 * respondem à mesma pergunta divergem no primeiro ajuste.
 *
 * ## "Tudo" e "Cartão" são a mesma pergunta com recortes diferentes
 *
 * *"Quanto gastamos, e em quê"* e *"quanto foi de cartão, e em quê"*. Duas
 * roscas empilhadas na mesma tela fariam a segunda parecer um detalhamento da
 * primeira, que não é: o gasto no débito não está na fatura. Um seletor de duas
 * pílulas troca o recorte sem mudar de lugar na tela.
 *
 * ## O "sempre mostrar subcategorias" é do APARELHO
 *
 * Quem tem categorias fundas quer as filhas sempre; quem não tem, nunca. É
 * preferência de quem olha, não da casa — e `useDeviceFlag` é onde essas moram
 * (o saldo escondido é a outra). Desligado por padrão: com todas as filhas à
 * mostra de saída, o primeiro olhar recebe vinte arcos.
 */
export function GastosPorCategoria({
  transactions,
  categories,
  /** O atalho para o filtro de "sem categoria" — só o Dashboard o mostra. */
  comAtalho = false,
}: {
  transactions: Transaction[]
  categories: Category[]
  comAtalho?: boolean
}) {
  const [visao, setVisao] = useState<'tudo' | 'cartao'>('tudo')
  const [sempreSubs, setSempreSubs] = useDeviceFlag('rosca-sempre-subs', false)
  const [destacada, setDestacada] = useState<string | null>(null)

  const usadas = visao === 'cartao' ? noCartao(transactions) : transactions
  const { totalCents, pais, subsPorPai } = anelDuplo(usadas, categories)
  // A fatia sem categoria é a de `id: null`. Medido na produção de hoje: ela é
  // **100%** da rosca — 2.462 das 2.728 transações não têm categoria. Uma rosca
  // de uma fatia cinza é uma observação verdadeira e inerte; com o atalho, ela
  // vira o que a nota chama de "o principal chamado à ação".
  const semCategoria = pais.find((slice) => slice.id === null)

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs text-[var(--text-muted)]">
          {visao === 'cartao'
            ? 'Gastos no cartão por categoria (competência)'
            : 'Gastos por categoria (competência)'}
        </h2>
        <div className="flex gap-1">
          {(['tudo', 'cartao'] as const).map((qual) => (
            /* A pílula pintada é MENOR que o alvo, como as abas de Produtos: o
               botão mantém os 44px e quem encolhe é a tinta. */
            <button
              key={qual}
              type="button"
              aria-pressed={visao === qual}
              onClick={() => {
                setVisao(qual)
                setDestacada(null)
              }}
              className="alvo"
            >
              <span
                className={`block rounded-full border px-3 py-1 text-xs ${
                  visao === qual
                    ? 'border-transparent bg-[var(--text)] font-medium text-[var(--bg)]'
                    : 'border-[var(--border)] text-[var(--text-muted)]'
                }`}
              >
                {qual === 'tudo' ? 'Tudo' : 'Cartão'}
              </span>
            </button>
          ))}
        </div>
      </div>

      <Donut
        shares={{ totalCents, slices: pais }}
        title="Gastos por categoria"
        subs={subsPorPai}
        destacada={destacada}
        onDestacar={setDestacada}
        sempreSubs={sempreSubs}
      />

      {/* Só quando há o que mostrar: numa casa sem categoria filha, o botão
          prometeria um anel que nunca aparece. */}
      {Object.keys(subsPorPai).length > 0 && (
        <p className="mt-3">
          <button
            type="button"
            role="switch"
            aria-checked={sempreSubs}
            onClick={() => {
              setSempreSubs(!sempreSubs)
              setDestacada(null)
            }}
            className="alvo justify-start gap-2 text-xs text-[var(--text-muted)]"
          >
            <span
              aria-hidden="true"
              className={`flex h-5 w-9 shrink-0 items-center rounded-full border p-0.5 transition-colors ${
                sempreSubs ? 'justify-end border-transparent' : 'justify-start border-[var(--border)]'
              }`}
              style={sempreSubs ? { background: 'var(--brand)' } : undefined}
            >
              <span
                className="block size-4 rounded-full"
                style={{ background: sempreSubs ? 'var(--bg)' : 'var(--text-faint)' }}
              />
            </span>
            Sempre mostrar subcategorias
          </button>
        </p>
      )}

      {comAtalho && semCategoria !== undefined && (
        <p className="mt-3 text-sm">
          <Link
            to="/transacoes"
            search={{ sem_categoria: 1 }}
            className="alvo justify-start underline"
          >
            {`Categorizar as transações sem categoria (${semCategoria.percent
              .toFixed(1)
              .replace('.', ',')}% do mês)`}
          </Link>
        </p>
      )}
    </>
  )
}
